<template>
    <div ref="chatbaseContainer" />
</template>

<script lang="ts" setup>
    import chatbaseService from '@/services/chatbase.service';
    import { useUserStore } from '@/stores/user.store';
    import { onBeforeUnmount, onMounted, ref } from 'vue';
    import { useApplicationStore } from '@/stores/application.store';

    const userStore = useUserStore();
    const applicationStore = useApplicationStore();
    const chatbaseContainer = ref<HTMLElement>();

    const getUserHash = async () => {
        return await chatbaseService.getUserHash();
    };

    const syncContact = async () => {
        return await chatbaseService.syncContact();
    };

    const initChatbase = async () => {
        if (!applicationStore.info?.tenant?.chatbase?.chatbotId) {
            return;
        }
        if (!userStore.user || !(await syncContact())) {
            return;
        }

        const userHash = await getUserHash();
        if (!userHash) {
            return;
        }

        window.chatbaseConfig = {
            chatbotId: applicationStore.info.tenant.chatbase.chatbotId,
        };

        window.chatbaseUserConfig = {
            user_id: userStore.user.id,
            user_hash: userHash,
            user_metadata: {
                name: `${userStore.user.first_name} ${userStore.user.last_name}`,
                email: userStore.user.email,
                account_type: userStore.type,
                organization_id: userStore.organizationId,
                is_notary: String(userStore.isNotary), // Convert boolean to string due to bug in Chatbase
                is_support: String(userStore.isSupport), // Convert boolean to string due to bug in Chatbase
                is_client: String(userStore.isClient), // Convert boolean to string due to bug in Chatbase
                is_advisor: String(userStore.isAdvisor), // Convert boolean to string due to bug in Chatbase
                is_admin: String(userStore.isAdmin), // Convert boolean to string
            },
        };

        const script = document.createElement('script');
        script.src = `https://${applicationStore.info.tenant.chatbase.domain}/embed.min.js`;
        script.async = true;

        script.onerror = (error) => {
            console.error('Error loading Chatbase script:', error);
        };

        if (chatbaseContainer.value) {
            chatbaseContainer.value.appendChild(script);
        }
    };

    const cleanup = () => {
        if (chatbaseContainer.value) {
            const script = chatbaseContainer.value.querySelector('script');

            if (script) {
                script.remove();
            }
        }

        delete window.chatbaseConfig;
        delete window.chatbaseUserConfig;
    };

    onMounted(async () => {
        await initChatbase();
    });

    onBeforeUnmount(() => {
        cleanup();
    });

    watch(
        () => userStore.user,
        async (newUser) => {
            cleanup();

            if (newUser) {
                await initChatbase();
            } else {
                const chatbaseIframe = document.querySelector(`iframe[src*=\"${applicationStore.info?.tenant?.chatbase?.domain}\"]`);

                if (chatbaseIframe) {
                    chatbaseIframe.remove();
                }
            }
        },
    );
</script>

<script lang="ts">
    declare global {
        interface Window {
            chatbaseConfig?: {
                chatbotId: string;
            };
            chatbaseUserConfig?: {
                user_id: string;
                user_hash: string;
                user_metadata: {
                    name: string;
                    email: string;
                    account_type: string;
                    organization_id: string;
                    is_notary: boolean | string;
                    is_support: boolean | string;
                    is_client: boolean | string;
                    is_advisor: boolean | string;
                    is_admin: boolean | string;
                };
            };
            chatbase?: {
                (action: string, config: any): void;
                setInitialMessages(messages: string[]): void;
            };
        }
    }
</script>

<style scoped>
    .chatbase-container {
        position: fixed;
        bottom: 20px;
        right: 20px;
        z-index: 1000;
    }
</style>
